import { api } from '../../lib/api-client';
import { FieldLink } from './FieldLink';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CrmRecord, IndexSort, IndexPreferences, SchemaDescriptor, SchemaField, SchemaObject, SchemaView } from '../../lib/api-types';
import { keys, recordsQuery, schemaQuery, useUpdateRecord } from '../../lib/queries';
import { useCurrentPageContext } from '../../lib/page-context';
import { markdownToPlainText } from '../content/MarkdownContent';
import { Icon } from '../ui/Icon';
import { RecordEditor } from './RecordEditor';
import styles from './Crm.module.css';
import { isNoteObject, NotesIndex } from '../notes/Notes';

const PAGE_SIZE = 100;

export function ObjectIndex({ objectName }: { objectName: string }) {
  return isNoteObject(objectName) ? <NotesIndex key={objectName} objectName={objectName} /> : <GeneralObjectIndex objectName={objectName} />;
}

function GeneralObjectIndex({ objectName }: { objectName: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const schema = useQuery(schemaQuery());
  const version = schema.data?.schemaVersion ?? 0;
  const object = schema.data?.objects.find(
    (candidate) => candidate.namePlural === objectName || candidate.name === objectName,
  );

  const savedSort = object?.indexPreferences?.sort;
  const sort = savedSort && object?.fields.some(field => field.name === savedSort.field) ? savedSort : undefined;
  const records = useQuery({
    ...recordsQuery(version, objectName, { limit: PAGE_SIZE, offset: 0, ...(sort ? { sortField: sort.field, sortDirection: sort.direction } : {}) }),
    enabled: Boolean(schema.data),
    placeholderData: () => undefined,
  });

  const boardView = object?.views.find((view) => view.type === 'board');
  const mode = object?.indexPreferences?.mode === 'board' && boardView ? 'board' : 'table';
  const [editingColumns, setEditingColumns] = useState(false);
  const savePreferences = useMutation({
    mutationFn: (input: { objectName: string; patch: IndexPreferences }) => api.updateIndexPreferences(input.objectName, input.patch),
    onSuccess: (result) => {
      queryClient.setQueryData<SchemaDescriptor>(keys.schema, current => current ? {
        ...current, objects: current.objects.map(candidate => candidate.name === result.object
          ? { ...candidate, indexPreferences: result.preferences } : candidate),
      } : current);
      void queryClient.invalidateQueries({ queryKey: keys.schema });
    },
  });
  useEffect(() => { setEditingColumns(false); setCreating(false); savePreferences.reset(); }, [objectName]);
  const save = (patch: IndexPreferences) => savePreferences.mutateAsync({ objectName, patch });
  const { setPageSpecificContext } = useCurrentPageContext();

  useEffect(() => {
    setPageSpecificContext({ viewType: mode, sorts: sort ? [sort] : [] });
    return () => setPageSpecificContext({});
  }, [mode, sort, setPageSpecificContext]);

  // The list endpoint's total is authoritative and may be newer than a schema
  // response already in the rail cache. Keep both visible counts in lockstep.
  useEffect(() => {
    const totalCount = records.data?.totalCount;
    if (totalCount === undefined || !object) return;
    queryClient.setQueryData<SchemaDescriptor>(keys.schema, (current) => {
      if (!current) return current;
      const target = current.objects.find((candidate) => candidate.id === object.id);
      if (!target || target.recordCount === totalCount) return current;
      return {
        ...current,
        objects: current.objects.map((candidate) => (
          candidate.id === object.id ? { ...candidate, recordCount: totalCount } : candidate
        )),
      };
    });
  }, [object, queryClient, records.data?.totalCount]);

  if (schema.isLoading) return <p className={styles.state}>Loading…</p>;
  if (!object) return <p className={styles.state}>No object named “{objectName}”.</p>;

  const rows = records.data?.records ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.toolbar}>
        <h1 className={styles.title}>
          <span className={styles.titleIcon}>
            <Icon name={object.icon ?? 'box'} size={20} />
          </span>
          {object.labelPlural}
          <span className={styles.count}>{records.data?.totalCount ?? object.recordCount}</span>
        </h1>
        <div className={styles.spacer} />
        <button type="button" className={styles.action} onClick={() => setEditingColumns(value => !value)} aria-expanded={editingColumns}>Columns</button>
        {boardView ? (
          <div className={styles.viewSwitch}>
            <button
              type="button"
              className={styles.viewButton}
              data-active={mode === 'table'}
              aria-pressed={mode === 'table'}
              disabled={savePreferences.isPending}
              onClick={() => { void save({ mode: 'table' }).catch(() => {}); }}
              aria-label="Table view"
            >
              <Icon name="list" size={15} />
            </button>
            <button
              type="button"
              className={styles.viewButton}
              data-active={mode === 'board'}
              aria-pressed={mode === 'board'}
              disabled={savePreferences.isPending}
              onClick={() => { void save({ mode: 'board' }).catch(() => {}); }}
              aria-label="Board view"
            >
              <Icon name="layout-board" size={15} />
            </button>
          </div>
        ) : null}
        <button type="button" className={`${styles.action} ${styles.actionPrimary}`} onClick={() => setCreating(true)}>
          <Icon name="plus" size={16} /> New {object.label}
        </button>
      </header>

      {creating && <RecordEditor key={object.id} object={object} version={version} onClose={() => setCreating(false)}
        onSaved={id => {
          setCreating(false);
          void navigate({ to: '/app/o/$object/$recordId', params: { object: object.namePlural, recordId: id } });
        }} />}

      {savePreferences.isError ? <p role="alert">Could not save your view: {savePreferences.error.message}</p> : null}
      {editingColumns ? <ColumnEditor key={object.id} object={object} saving={savePreferences.isPending}
        onSave={async fields => { await save({ fields }); setEditingColumns(false); }}
        onCancel={() => setEditingColumns(false)} /> : null}
      <div className={styles.body}>
        {records.isError ? <p role="alert" className={styles.state}>Could not load records: {records.error.message}</p> : records.isLoading ? (
          <p className={styles.state}>Loading…</p>
        ) : rows.length === 0 ? (
          <div className={styles.state}>No {object.labelPlural.toLowerCase()} yet.
            <button type="button" className={`${styles.action} ${styles.emptyCreate}`} onClick={() => setCreating(true)}>Create your first {object.label.toLowerCase()}</button>
          </div>
        ) : mode === 'board' && boardView ? (
          <BoardView key={object.id} object={object} objectName={objectName} version={version} view={boardView} records={rows} />
        ) : (
          <TableView object={object} records={rows} sort={sort} saving={savePreferences.isPending}
            onSort={field => { void save({ sort: { field, direction: sort?.field === field && sort.direction === 'asc' ? 'desc' : 'asc' } }).catch(() => {}); }} />
        )}
      </div>
    </div>
  );
}

/** Personal choices override the shared defaults, preserving exact order. */
export function tableColumns(object: SchemaObject): SchemaField[] {
  const tableView = object.views.find((view) => view.type === 'table');
  const legacyColumns = tableView?.config.columns;
  const configured = object.indexPreferences?.fields ?? tableView?.config.fields ??
    legacyColumns?.filter(column => column.visible !== false).map(column => column.field);
  const byName = new Map(object.fields.map((field) => [field.name, field]));
  if (configured) {
    const fields = configured.map(name => byName.get(name)).filter((field): field is SchemaField => Boolean(field));
    if (fields.length) return fields;
  }
  const defaults = object.fields.filter(field => field.type !== 'position' && !field.isSystem).slice(0, 6);
  const label = byName.get(object.labelField);
  return label ? [label, ...defaults.filter(field => field !== label)] : defaults;
}

function ColumnEditor({ object, saving, onSave, onCancel }: {
  object: SchemaObject; saving: boolean; onSave: (fields: string[]) => Promise<void>; onCancel: () => void;
}) {
  const [selected, setSelected] = useState(() => tableColumns(object).map(field => field.name));
  const move = (index: number, delta: number) => setSelected(current => {
    const next = [...current];
    [next[index], next[index + delta]] = [next[index + delta]!, next[index]!];
    return next;
  });
  return <section className={styles.columnEditor} aria-label="Your index columns">
    <p>Choose columns for your list view, in display order.</p>
    <ol>{selected.map((name, index) => <li key={name}>
      <span>{object.fields.find(field => field.name === name)?.label ?? name}</span>
      <button type="button" disabled={saving || index === 0} onClick={() => move(index, -1)} aria-label={`Move ${name} up`}>↑</button>
      <button type="button" disabled={saving || index === selected.length - 1} onClick={() => move(index, 1)} aria-label={`Move ${name} down`}>↓</button>
      <button type="button" disabled={saving} onClick={() => setSelected(current => current.filter(field => field !== name))} aria-label={`Hide ${name}`}>Hide</button>
    </li>)}</ol>
    <select aria-label="Add column" value="" disabled={saving} onChange={event => { const name = event.target.value; setSelected(current => [...current, name]); }}>
      <option value="" disabled>Add column…</option>
      {object.fields.filter(field => !selected.includes(field.name)).map(field => <option key={field.id} value={field.name}>{field.label}</option>)}
    </select>
    <div className={styles.columnActions}>
      <button type="button" className={styles.action} disabled={saving || selected.length === 0} onClick={() => { void onSave(selected).catch(() => {}); }}>{saving ? 'Saving…' : 'Save columns'}</button>
      <button type="button" className={styles.action} disabled={saving} onClick={onCancel}>Cancel</button>
    </div>
  </section>;
}

function TableView({ object, records, sort, saving, onSort }: {
  object: SchemaObject; records: CrmRecord[]; sort: IndexSort | undefined; saving: boolean; onSort: (field: string) => void;
}) {
  const navigate = useNavigate();
  const columns = useMemo(() => tableColumns(object), [object]);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {columns
            .map((column) => (
              <th key={column.id} scope="col" aria-sort={sort?.field === column.name ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'}>
                <button type="button" className={styles.sortHeader} disabled={saving} onClick={() => onSort(column.name)}
                  aria-label={`Sort by ${column.label} ${sort?.field === column.name && sort.direction === 'asc' ? 'descending' : 'ascending'}`}>
                  {column.label}<span aria-hidden="true">{sort?.field === column.name ? sort.direction === 'asc' ? ' ↑' : ' ↓' : ''}</span>
                </button>
              </th>
            ))}
        </tr>
      </thead>
      <tbody>
        {records.map((record) => {
          const byName = new Map(record.fields.map((field) => [field.name, field]));
          return (
            <tr
              key={record.id}
              className={styles.row}
              onClick={() =>
                void navigate({
                  to: '/app/o/$object/$recordId',
                  params: { object: object.namePlural, recordId: record.id },
                })
              }
            >
              {columns
                    .map((column) => (
                  <td key={column.id} className={column.name === object.labelField ? styles.primaryCell : undefined}>{column.name === object.labelField ? record.displayName || 'Untitled' : <FieldLink value={fieldPreview(byName.get(column.name))} />}</td>
                ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function BoardView({
  object,
  objectName,
  version,
  view,
  records,
}: {
  object: SchemaObject;
  objectName: string;
  version: number;
  view: SchemaView;
  records: CrmRecord[];
}) {
  const groupBy = view.config.groupBy ?? view.config.groupByField;
  const groupField = object.fields.find((field) => field.name === groupBy);
  const choices = (groupField?.options.choices as Array<{ value: string; label: string }> | undefined) ?? [];
  const updateRecord = useUpdateRecord(version, objectName);
  const draggedId = useRef<string | null>(null);
  const saving = useRef(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null | undefined>(undefined);
  const canMove = Boolean(groupField) && !updateRecord.isPending;

  const endDrag = () => {
    draggedId.current = null;
    setDragging(null);
    setDropTarget(undefined);
  };

  const moveRecord = async (value: string | null) => {
    const record = records.find(candidate => candidate.id === draggedId.current);
    endDrag();
    if (!record || !groupBy || !canMove || saving.current || (value === null && groupField?.isRequired)) return;
    if ((record.data[groupBy] ?? null) === value) return;
    saving.current = true;
    try {
      await updateRecord.mutateAsync({ id: record.id, data: { [groupBy]: value } });
    } catch {
      // The mutation error is shown below; leave the card in its saved category.
    } finally {
      saving.current = false;
    }
  };

  const columns = useMemo(() => {
    const buckets = new Map<string, CrmRecord[]>();
    for (const choice of choices) buckets.set(choice.value, []);
    const unassigned: CrmRecord[] = [];
    for (const record of records) {
      const value = groupBy ? record.data[groupBy] : null;
      const key = typeof value === 'string' ? value : null;
      if (key && buckets.has(key)) buckets.get(key)?.push(record);
      else if (key) buckets.set(key, [record]);
      else unassigned.push(record);
    }
    const result: Array<{ value: string | null; label: string; records: CrmRecord[] }> = [...buckets.entries()].map(([value, items]) => ({
      value,
      label: choices.find((choice) => choice.value === value)?.label ?? value,
      records: items,
    }));
    if (unassigned.length || (groupField && !groupField.isRequired)) result.push({ value: null, label: 'No value', records: unassigned });
    return result;
  }, [choices, groupBy, groupField, records]);

  return (
    <>
      {updateRecord.isError ? <p role="alert" className={styles.state}>Could not move card: {updateRecord.error.message}</p> : null}
      {updateRecord.isPending ? <p role="status">Saving category…</p> : null}
      <div className={styles.board}>
        {columns.map((column) => (
          <section key={JSON.stringify(column.value)} className={styles.column}
            aria-label={column.label}
            data-drop-target={dropTarget === column.value}
            onDragOver={event => {
              if (!draggedId.current || !canMove || (column.value === null && groupField?.isRequired)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              setDropTarget(column.value);
            }}
            onDragLeave={event => {
              if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setDropTarget(undefined);
            }}
            onDrop={event => {
              if (!draggedId.current) return;
              event.preventDefault();
              void moveRecord(column.value);
            }}
          >
            <header className={styles.columnHeader}>
              {column.label}
              <span className={styles.columnCount}>{column.records.length}</span>
            </header>
            <div className={styles.columnBody}>
              {column.records.map((record) => (
                <Link
                  key={record.id}
                  to="/app/o/$object/$recordId"
                  params={{ object: object.namePlural, recordId: record.id }}
                  className={styles.card}
                  draggable={canMove}
                  data-dragging={dragging === record.id}
                  onDragStart={event => {
                    if (!canMove) { event.preventDefault(); return; }
                    updateRecord.reset();
                    draggedId.current = record.id;
                    setDragging(record.id);
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', record.id);
                  }}
                  onDragEnd={endDrag}
                >
                  <div className={styles.cardTitle}>{record.displayName || 'Untitled'}</div>
                  <BoardCardMeta record={record} object={object} groupBy={groupBy} />
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function BoardCardMeta({
  record,
  object,
  groupBy,
}: {
  record: CrmRecord;
  object: SchemaObject;
  groupBy: string | undefined;
}) {
  // One supporting line: the first non-grouping, non-label field with a value.
  const meta = record.fields.find(
    (field) =>
      field.name !== groupBy &&
      field.name !== object.labelField &&
      field.type !== 'position' &&
      Boolean(field.displayValue),
  );
  if (!meta) return null;
  return <div className={styles.cardMeta}>{fieldPreview(meta)}</div>;
}

function fieldPreview(field: CrmRecord['fields'][number] | undefined): string {
  if (!field?.displayValue) return '';
  return field.type === 'markdown' ? markdownToPlainText(field.displayValue) : field.displayValue;
}
